import { create } from 'zustand';

export const useHistoryStore = create((set, get) => ({
  // 历史记录栈
  history: [],
  currentIndex: -1,
  maxHistorySize: 50,

  // 添加历史记录
  addHistory: (action) => {
    const { history, currentIndex, maxHistorySize } = get();
    
    // 如果当前不在历史记录的末尾，删除后面的记录
    const newHistory = history.slice(0, currentIndex + 1);
    
    // 添加新的历史记录
    newHistory.push({
      id: Date.now(),
      timestamp: new Date().toISOString(),
      action,
      ...action.data
    });

    // 限制历史记录大小
    if (newHistory.length > maxHistorySize) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      currentIndex: newHistory.length - 1
    });
  },

  // 撤销操作
  undo: () => {
    const { history, currentIndex } = get();
    
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const actionToUndo = history[currentIndex];
      
      // 执行撤销逻辑
      get().executeUndo(actionToUndo);
      
      set({ currentIndex: newIndex });
      return true;
    }
    return false;
  },

  // 重做操作
  redo: () => {
    const { history, currentIndex } = get();
    
    if (currentIndex < history.length - 1) {
      const newIndex = currentIndex + 1;
      const actionToRedo = history[newIndex];
      
      // 执行重做逻辑
      get().executeRedo(actionToRedo);
      
      set({ currentIndex: newIndex });
      return true;
    }
    return false;
  },

  // 执行撤销逻辑
  executeUndo: (action) => {
    const { useTimelineStore } = require('./timelineStore');
    
    switch (action.action.type) {
      case 'ADD_CLIP':
        // 撤销添加片段
        useTimelineStore.getState().deleteClip(action.trackId, action.clipId);
        break;
        
      case 'DELETE_CLIP':
        // 撤销删除片段（重新添加）
        useTimelineStore.getState().addClipToTrack(action.trackId, action.clipData);
        break;
        
      case 'MOVE_CLIP':
        // 撤销移动片段
        useTimelineStore.getState().moveClipToTrack(
          action.clipId, 
          action.toTrackId, 
          action.fromTrackId, 
          action.fromPosition
        );
        break;
        
      case 'UPDATE_CLIP':
        // 撤销更新片段
        useTimelineStore.getState().updateClip(
          action.trackId, 
          action.clipId, 
          action.oldData
        );
        break;
        
      case 'CREATE_TRACK':
        // 撤销创建轨道
        useTimelineStore.getState().deleteTrack(action.trackId);
        break;
        
      case 'DELETE_TRACK':
        // 撤销删除轨道（重新创建）
        useTimelineStore.getState().restoreTrack(action.trackData);
        break;
        
      case 'REORDER_TRACKS':
        // 撤销轨道重排
        useTimelineStore.getState().reorderTracks(action.toIndex, action.fromIndex);
        break;
        
      case 'REORDER_CLIPS':
        // 撤销片段重排
        useTimelineStore.getState().reorderClipsInTrack(
          action.trackId, 
          action.toIndex, 
          action.fromIndex
        );
        break;
        
      default:
        console.warn('未知的撤销操作类型:', action.action.type);
    }
  },

  // 执行重做逻辑
  executeRedo: (action) => {
    const { useTimelineStore } = require('./timelineStore');
    
    switch (action.action.type) {
      case 'ADD_CLIP':
        // 重做添加片段
        useTimelineStore.getState().addClipToTrack(action.trackId, action.clipData);
        break;
        
      case 'DELETE_CLIP':
        // 重做删除片段
        useTimelineStore.getState().deleteClip(action.trackId, action.clipId);
        break;
        
      case 'MOVE_CLIP':
        // 重做移动片段
        useTimelineStore.getState().moveClipToTrack(
          action.clipId, 
          action.fromTrackId, 
          action.toTrackId, 
          action.toPosition
        );
        break;
        
      case 'UPDATE_CLIP':
        // 重做更新片段
        useTimelineStore.getState().updateClip(
          action.trackId, 
          action.clipId, 
          action.newData
        );
        break;
        
      case 'CREATE_TRACK':
        // 重做创建轨道
        useTimelineStore.getState().createCustomTrack(action.trackName);
        break;
        
      case 'DELETE_TRACK':
        // 重做删除轨道
        useTimelineStore.getState().deleteTrack(action.trackId);
        break;
        
      case 'REORDER_TRACKS':
        // 重做轨道重排
        useTimelineStore.getState().reorderTracks(action.fromIndex, action.toIndex);
        break;
        
      case 'REORDER_CLIPS':
        // 重做片段重排
        useTimelineStore.getState().reorderClipsInTrack(
          action.trackId, 
          action.fromIndex, 
          action.toIndex
        );
        break;
        
      default:
        console.warn('未知的重做操作类型:', action.action.type);
    }
  },

  // 检查是否可以撤销
  canUndo: () => {
    const { currentIndex } = get();
    return currentIndex > 0;
  },

  // 检查是否可以重做
  canRedo: () => {
    const { history, currentIndex } = get();
    return currentIndex < history.length - 1;
  },

  // 获取当前历史记录信息
  getHistoryInfo: () => {
    const { history, currentIndex } = get();
    return {
      total: history.length,
      current: currentIndex + 1,
      canUndo: get().canUndo(),
      canRedo: get().canRedo()
    };
  },

  // 清空历史记录
  clearHistory: () => set({
    history: [],
    currentIndex: -1
  }),

  // 获取历史记录列表（用于调试）
  getHistoryList: () => {
    const { history } = get();
    return history.map((item, index) => ({
      index,
      type: item.action.type,
      timestamp: item.timestamp,
      description: item.action.description || item.action.type
    }));
  }
}));

// 创建历史记录的辅助函数
export const createHistoryAction = (type, description, data = {}) => ({
  type,
  description,
  data
});

// 常用的历史记录动作类型
export const HISTORY_ACTIONS = {
  ADD_CLIP: 'ADD_CLIP',
  DELETE_CLIP: 'DELETE_CLIP',
  MOVE_CLIP: 'MOVE_CLIP',
  UPDATE_CLIP: 'UPDATE_CLIP',
  TRIM_CLIP: 'TRIM_CLIP',
  CREATE_TRACK: 'CREATE_TRACK',
  DELETE_TRACK: 'DELETE_TRACK',
  RENAME_TRACK: 'RENAME_TRACK',
  REORDER_TRACKS: 'REORDER_TRACKS',
  REORDER_CLIPS: 'REORDER_CLIPS'
};